import React, { useEffect, useState } from "react";
import styled from "styled-components";
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import InputAdornment from '@material-ui/core/InputAdornment';
import TextField from '@material-ui/core/TextField';
import Checkbox from '@material-ui/core/Checkbox';

import { UnitType, VenueType, UIStyle, APIIntervention } from '../models'
import { pickTimeUnit, isPlural } from '../utils/ui.utils'
import {
  serverToLocalDate,
  localToServerDate,
  unitTimeConverter,
  getTimeZonesList,
  gmtFormat
} from '../utils/date.utils'
import {
  Flex,
  SectionWrapper,
  SecondaryHeader,
  FormCard,
  FieldGroup,
  Label,
  HelpTextInline
} from '../styles/global';
import { Blackout } from './blackOutPeriods';
import { Group } from './groupSelections';
import { ShopItem } from './shopItems';
import { DateInput } from '../components/DateInput'

import { Interventions } from './Interventions'
import { getInterventions } from "../utils/api.utils";
import { useIsComponentMounted } from "../utils/hooks.utils";

interface Props {
  currentSimulation: any;
  setSimulation: any;
}

export const GlobalSettings = ({ currentSimulation, setSimulation }: Props) => {
  const isComponentMounted = useIsComponentMounted();
  const [foodOptions, setFoodOptions] = useState<any>(currentSimulation.food_support);
  const [sequenceOptions, setSequenceOptions] = useState<any>(currentSimulation.sequence_support);
  const [groupOptions, setGroupOptions] = useState<any>(currentSimulation.group_support);
  const [shopOptions, setShopOptions] = useState<any>(currentSimulation.shop_support);
  const [interventionOptions, setInterventionOptions] = useState<any>(currentSimulation.intervention_support);
  const [tzOptions] = useState<any[]>(getTimeZonesList());

  const showFoodOptions = foodOptions === true;
  const showSequenceOptions = sequenceOptions === true;
  const showGroupOptions = groupOptions === true;
  const showShopOptions = shopOptions === true;
  const showInterventionOptions = interventionOptions === true;
  let timeText = pickTimeUnit(currentSimulation.unit_type);

  const [interventions, setInterventions] = useState<APIIntervention[]>([]);
  const interventionsLoaded = interventions && interventions.length >= 1;

  // Ensure defaults for optional/missing parameters during render
  const safeSimulation = {
    ...currentSimulation,
    global_sim: currentSimulation.global_sim ?? false,
    tutorial_url: currentSimulation.tutorial_url ?? "",
    qr_support: currentSimulation.qr_support ?? false,
    leaderboard_support: currentSimulation.leaderboard_support ?? false,
    raffle_support: currentSimulation.raffle_support ?? false,
    food_support: currentSimulation.food_support ?? false,
    food_interval: Number(currentSimulation.food_interval ?? 0.5),
    sequence_support: currentSimulation.sequence_support ?? false,
    genome_coverage: Number(currentSimulation.genome_coverage ?? 100),
    score_support: currentSimulation.score_support ?? true,
    debug_support: currentSimulation.debug_support ?? false,
    escape_limit: Number(currentSimulation.escape_limit ?? 300),
    ui_style: currentSimulation.ui_style ?? 0,
    group_support: currentSimulation.group_support ?? false,
    random_group: currentSimulation.random_group ?? false,
    shop_support: currentSimulation.shop_support ?? false,
    join_window: Number(currentSimulation.join_window ?? 100),
    intervention_support: currentSimulation.intervention_support ?? false,
    interventions: currentSimulation.interventions ?? [],
    rewards: {
      ...currentSimulation.rewards,
      ppe_period: Number(currentSimulation.rewards?.ppe_period ?? 0.5),
      mask_period: Number(currentSimulation.rewards?.mask_period ?? 1),
      min_questions: Number(currentSimulation.rewards?.min_questions ?? 3),
      min_score_mask: Number(currentSimulation.rewards?.min_score_mask ?? 60),
      min_score_ppe: Number(currentSimulation.rewards?.min_score_ppe ?? 100),
    }
  };

  const fetchInterventions = () => {
    getInterventions()
      .then((result: any) => {
        if (isComponentMounted.current) {
          const payload = result as any;
          setInterventions(payload);
        }
      })
      .catch((err: any) => {
        console.error(err);
        if (isComponentMounted.current) {
          setInterventions([])
        }
      });
  };

  useEffect(() => {
    fetchInterventions();
  }, []);

  return (
    <div style={{ animation: 'fadeIn 0.3s ease-out' }}>

      {/* Identity Section */}
      <SectionWrapper>
        <SecondaryHeader>Identity & Access</SecondaryHeader>
        <FormCard>
          <Flex gap="24px">
            <FieldGroup style={{ flex: 2 }}>
              <Label>Simulation Name *</Label>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="My Simulation"
                required
                value={safeSimulation.name}
                onChange={(e) => setSimulation((curr: any) => ({ ...curr, name: e.target.value }))}
              />
            </FieldGroup>
            <FieldGroup style={{ flex: 1 }}>
              <Label>Sim Code *</Label>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="mysimcode"
                required
                value={safeSimulation.code}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9a-z]/gi, '');
                  setSimulation((curr: any) => ({ ...curr, code: val }));
                }}
              />
              <HelpTextInline>(letters and numbers only)</HelpTextInline>
            </FieldGroup>
            <FieldGroup style={{ flex: 1 }}>
              <Label>Global Simulation</Label>
              <RadioGroup
                row
                value={safeSimulation.global_sim}
                onChange={(e) => setSimulation((curr: any) => ({ ...curr, global_sim: e.target.value === 'true' }))}
              >
                <FormControlLabel value={true} control={<Radio color="primary" />} label="Yes" />
                <FormControlLabel value={false} control={<Radio color="primary" />} label="No" />
              </RadioGroup>
              <HelpTextInline>
                Simulation for all users across timezones (experimental)<br />
                Requires app support
              </HelpTextInline>
            </FieldGroup>
          </Flex>

          <FieldGroup>
            <Label>Consent URL</Label>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="https://epidemica.info/consent"
              value={safeSimulation.activity_url}
              onChange={(e) => setSimulation((curr: any) => ({ ...curr, activity_url: e.target.value }))}
            />
          </FieldGroup>

          <FieldGroup>
            <Label>Tutorial URL</Label>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="https://example.com/tutorial"
              value={safeSimulation.tutorial_url}
              onChange={(e) => setSimulation((curr: any) => ({ ...curr, tutorial_url: e.target.value }))}
            />
          </FieldGroup>

          <Flex gap="24px">
            <FieldGroup style={{ flex: 1 }}>
              <Label>Avatar Style</Label>
              <Select
                fullWidth
                variant="outlined"
                value={safeSimulation.ui_style}
                onChange={(e) => setSimulation((curr: any) => ({ ...curr, ui_style: e.target.value }))}
              >
                <MenuItem value={UIStyle.DEFAULT}>Default</MenuItem>
                <MenuItem value={UIStyle.LEGACY}>Legacy</MenuItem>
              </Select>
            </FieldGroup>
            <FieldGroup style={{ flex: 1 }}>
              <Label>Select Time Zone</Label>
              <Select
                fullWidth
                id="tz-select"
                variant="outlined"
                MenuProps={{ getContentAnchorEl: null }}
                value={safeSimulation.time_zone_IANA ?? ""}
                onChange={(e) => {
                  const val: any = e.target.value;
                  setSimulation((curr: any) => {
                    // Preserve the existing dates - don't let them get cleared during timezone change
                    const updatedSim = {
                      ...curr,
                      time_zone: gmtFormat(val),
                      time_zone_IANA: val
                    };
                    
                    // Ensure dates remain valid after timezone change
                    if (curr.start_date && !updatedSim.start_date) {
                      updatedSim.start_date = curr.start_date;
                    }
                    if (curr.end_date && !updatedSim.end_date) {
                      updatedSim.end_date = curr.end_date;
                    }
                    
                    return updatedSim;
                  });
                }}
              >
                {tzOptions.map((tz, index) => <MenuItem key={index} value={tz}>{tz}</MenuItem>)}
              </Select>
            </FieldGroup>
          </Flex>
        </FormCard>
      </SectionWrapper>

      {/* Schedule Section */}
      <SectionWrapper>
        <SecondaryHeader>Schedule & Capacity</SecondaryHeader>
        <FormCard>
          <Flex gap="24px" alignItems="center">
            <FieldGroup style={{ flex: 1 }}>
              <Label>Start Date/Time *</Label>
              <DateInput
                label="Start Date/Time"
                setWidth="100%"
                setValue={(value: string) => {
                  const converted = localToServerDate(value, safeSimulation.time_zone_IANA);
                  // Only update if we have a valid date - prevent clearing on timezone change
                  if (converted) {
                    setSimulation((current: any) => ({
                      ...current,
                      start_date: converted
                    }));
                  }
                }}
                value={serverToLocalDate(safeSimulation.start_date, safeSimulation.time_zone_IANA)}
                required
              />
            </FieldGroup>
            <div style={{ marginTop: '12px', fontSize: '20px', color: '#cbd5e0' }}>—</div>
            <FieldGroup style={{ flex: 1 }}>
              <Label>End Date/Time *</Label>
              <DateInput
                label="End Date/Time"
                setWidth="100%"
                setValue={(value: string) => {
                  const converted = localToServerDate(value, safeSimulation.time_zone_IANA);
                  // Only update if we have a valid date - prevent clearing on timezone change
                  if (converted) {
                    setSimulation((current: any) => ({
                      ...current,
                      end_date: converted
                    }));
                  }
                }}
                value={serverToLocalDate(safeSimulation.end_date, safeSimulation.time_zone_IANA)}
                required
              />
            </FieldGroup>
          </Flex>

          <FieldGroup style={{ marginTop: '16px' }}>
            <Label>Add Blackout</Label>
            <Blackout
              blackouts={safeSimulation.pauseDates}
              setValue={(value: any[]) => setSimulation((current: any) => ({ ...current, pauseDates: value }))}
            />
          </FieldGroup>

          <Flex gap="24px" style={{ marginTop: '24px' }}>
            <FieldGroup style={{ flex: 1 }}>
              <Label>Total Size of Simulation *</Label>
              <TextField
                fullWidth
                type="number"
                variant="outlined"
                value={safeSimulation.size}
                onChange={(e) => {
                  const val = e.target.value === "" ? 0 : Number(e.target.value);
                  setSimulation((current: any) => ({ ...current, size: val, case_zero_population_size: val }));
                }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">participants</InputAdornment>,
                }}
              />
            </FieldGroup>
            <FieldGroup style={{ flex: 1 }}>
              <Label>Venue</Label>
              <Select
                fullWidth
                variant="outlined"
                value={safeSimulation.venue ?? ""}
                onChange={(e) => setSimulation((current: any) => ({ ...current, venue: e.target.value }))}
              >
                <MenuItem value={VenueType.CLASSROOM}>Classroom</MenuItem>
                <MenuItem value={VenueType.MEDIUM}>Medium</MenuItem>
                <MenuItem value={VenueType.LARGE}>Large</MenuItem>
              </Select>
            </FieldGroup>
          </Flex>

          <Flex gap="24px">
            <FieldGroup style={{ flex: 1 }}>
              <Label>Number of Strains *</Label>
              <TextField
                fullWidth
                type="number"
                variant="outlined"
                value={safeSimulation.number_of_strains}
                inputProps={{ min: 0, max: 1 }}
                onChange={(e) => setSimulation((curr: any) => ({ ...curr, number_of_strains: e.target.value === "" ? 0 : Number(e.target.value) }))}
              />
            </FieldGroup>
            <FieldGroup style={{ flex: 1 }}>
              <Label>Join Window Percentage *</Label>
              <TextField
                fullWidth
                type="number"
                variant="outlined"
                value={safeSimulation.join_window}
                onChange={(e) => setSimulation((current: any) => ({ ...current, join_window: e.target.value === "" ? 0 : Number(e.target.value) }))}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
              />
            </FieldGroup>
          </Flex>
        </FormCard>
      </SectionWrapper>

      {/* Logic & Infection Section */}
      <SectionWrapper>
        <SecondaryHeader>Parameters & Logic</SecondaryHeader>
        <FormCard>
          <Flex gap="24px">
            <FieldGroup style={{ flex: 1 }}>
              <Label>Time unit of time-related parameters</Label>
              <Select
                fullWidth
                variant="outlined"
                value={safeSimulation.unit_type ?? ""}
                onChange={(e) => {
                  const value = e.target.value;
                  setSimulation((current: any) => ({
                    ...current,
                    previousType: safeSimulation.unit_type,
                    unit_type: value,
                    case_zero_adapt_time: unitTimeConverter(current.unit_type, current.case_zero_adapt_time, value),
                    case_zero_infect_time: unitTimeConverter(current.unit_type, current.case_zero_infect_time, value),
                    quarantine_interval: unitTimeConverter(current.unit_type, current.quarantine_interval, value),
                    quarantine_wait_time: unitTimeConverter(current.unit_type, current.quarantine_wait_time, value),
                    rewards: {
                      ...current.rewards,
                      ppe_period: unitTimeConverter(current.unit_type, current.rewards.ppe_period, value),
                      mask_period: unitTimeConverter(current.unit_type, current.rewards.mask_period, value)
                    }
                  }));
                }}
              >
                <MenuItem value={UnitType.SECOND}>{pickTimeUnit(UnitType.SECOND)}s</MenuItem>
                <MenuItem value={UnitType.MINUTE}>{pickTimeUnit(UnitType.MINUTE)}s</MenuItem>
                <MenuItem value={UnitType.HOUR}>{pickTimeUnit(UnitType.HOUR)}s</MenuItem>
                <MenuItem value={UnitType.DAY}>{pickTimeUnit(UnitType.DAY)}s</MenuItem>
              </Select>
            </FieldGroup>
            <FieldGroup style={{ flex: 1 }}>
              <Label>Simulation time step *</Label>
              <TextField
                fullWidth
                type="number"
                variant="outlined"
                value={safeSimulation.time_step}
                onChange={(e) => setSimulation((current: any) => ({ ...current, time_step: e.target.value === "" ? 0 : Number(e.target.value) }))}
                InputProps={{
                  endAdornment: <InputAdornment position="end">{isPlural(safeSimulation.time_step, pickTimeUnit(UnitType.SECOND))}</InputAdornment>,
                }}
              />
            </FieldGroup>
          </Flex>

          <FieldGroup>
            <Label>EPI Generations *</Label>
            <TextField
              fullWidth
              type="number"
              variant="outlined"
              value={safeSimulation.epi_generations}
              onChange={(e) => setSimulation((current: any) => ({ ...current, epi_generations: e.target.value === "" ? 0 : Number(e.target.value) }))}
            />
          </FieldGroup>

          <FieldGroup style={{ padding: '24px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0', marginTop: '16px' }}>
            <Label>Contact Rate *</Label>
            <Flex gap="16px" alignItems="center">
              <TextField
                style={{ width: '120px' }}
                type="number"
                variant="outlined"
                value={safeSimulation.number_of_contacts}
                onChange={(e) => setSimulation((current: any) => ({ ...current, number_of_contacts: e.target.value === "" ? 0 : Number(e.target.value) }))}
              />
              <span style={{ fontWeight: 600, color: '#718096' }}>contact in</span>
              <TextField
                style={{ width: '180px' }}
                type="number"
                variant="outlined"
                value={safeSimulation.number_of_contacts_in}
                onChange={(e) => setSimulation((current: any) => ({ ...current, number_of_contacts_in: e.target.value === "" ? 0 : Number(e.target.value) }))}
                InputProps={{
                  endAdornment: <InputAdornment position="end">{isPlural(safeSimulation.number_of_contacts_in, pickTimeUnit(UnitType.MINUTE))}</InputAdornment>,
                }}
              />
            </Flex>
          </FieldGroup>

          <div style={{ background: '#ebf8ff', padding: '24px', borderRadius: '12px', border: '1px solid #bee3f8' }}>
            <Flex gap="24px" style={{ marginBottom: '16px' }}>
              <FieldGroup style={{ flex: 1 }}>
                <Label>Number of automatically generated index cases *</Label>
                <TextField
                  fullWidth
                  type="number"
                  variant="outlined"
                  value={safeSimulation.case_zero_count}
                  onChange={(e) => setSimulation((curr: any) => ({ ...curr, case_zero_count: e.target.value === "" ? 0 : Number(e.target.value) }))}
                  InputProps={{ endAdornment: <InputAdornment position="end">people</InputAdornment> }}
                />
              </FieldGroup>
              <FieldGroup style={{ flex: 1 }}>
                <Label>Time to automatically generate index cases *</Label>
                <TextField
                  fullWidth
                  type="number"
                  variant="outlined"
                  value={safeSimulation.case_zero_infect_time}
                  onChange={(e) => setSimulation((curr: any) => ({ ...curr, case_zero_infect_time: e.target.value === "" ? 0 : Number(e.target.value) }))}
                  InputProps={{ endAdornment: <InputAdornment position="end">{isPlural(safeSimulation.case_zero_infect_time, timeText)}</InputAdornment> }}
                />
              </FieldGroup>
            </Flex>
          </div>

        </FormCard>
      </SectionWrapper>

      {/* Enabled Features Section */}
      <SectionWrapper>
        <SecondaryHeader>Modules & Hardware</SecondaryHeader>
        <FormCard>
          <GridContainer>
            <FeatureToggle>
              <Label>Quiz is taken via QR Code</Label>
              <RadioGroup
                row
                value={safeSimulation.qr_support}
                onChange={(e) => setSimulation((curr: any) => ({ ...curr, qr_support: e.target.value === 'true' }))}
              >
                <FormControlLabel value={true} control={<Radio color="primary" />} label="Yes" />
                <FormControlLabel value={false} control={<Radio color="primary" />} label="No" />
              </RadioGroup>
            </FeatureToggle>

            <FeatureToggle>
              <Label>Leaderboard feature is enabled</Label>
              <RadioGroup
                row
                value={safeSimulation.leaderboard_support}
                onChange={(e) => setSimulation((curr: any) => ({ ...curr, leaderboard_support: e.target.value === 'true' }))}
              >
                <FormControlLabel value={true} control={<Radio color="primary" />} label="Yes" />
                <FormControlLabel value={false} control={<Radio color="primary" />} label="No" />
              </RadioGroup>
            </FeatureToggle>

            <FeatureToggle>
              <Label>Raffle feature is enabled</Label>
              <RadioGroup
                row
                value={safeSimulation.raffle_support}
                onChange={(e) => setSimulation((curr: any) => ({ ...curr, raffle_support: e.target.value === 'true' }))}
              >
                <FormControlLabel value={true} control={<Radio color="primary" />} label="Yes" />
                <FormControlLabel value={false} control={<Radio color="primary" />} label="No" />
              </RadioGroup>
            </FeatureToggle>

            <FeatureToggle>
              <Label>Quiz score determines illness recovery</Label>
              <RadioGroup
                row
                value={safeSimulation.score_support}
                onChange={(e) => setSimulation((curr: any) => ({ ...curr, score_support: e.target.value === 'true' }))}
              >
                <FormControlLabel value={true} control={<Radio color="primary" />} label="Yes" />
                <FormControlLabel value={false} control={<Radio color="primary" />} label="No" />
              </RadioGroup>
            </FeatureToggle>

            <FeatureToggle>
              <Label>Sequence Features are enabled</Label>
              <RadioGroup
                row
                value={safeSimulation.sequence_support}
                onChange={(e) => {
                  const val = e.target.value === 'true';
                  setSimulation((curr: any) => ({ ...curr, sequence_support: val }));
                  setSequenceOptions(val);
                }}
              >
                <FormControlLabel value={true} control={<Radio color="primary" />} label="Yes" />
                <FormControlLabel value={false} control={<Radio color="primary" />} label="No" />
              </RadioGroup>
            </FeatureToggle>

            {showSequenceOptions && (
              <FeatureToggle className="sub-feature">
                <Label>Genome Coverage %</Label>
                <TextField
                  type="number"
                  variant="outlined"
                  style={{ width: '120px' }}
                  value={safeSimulation.genome_coverage}
                  onChange={(e) => setSimulation((curr: any) => ({ ...curr, genome_coverage: e.target.value === "" ? 0 : Number(e.target.value) }))}
                />
              </FeatureToggle>
            )}

            <FeatureToggle>
              <Label>Food plays a role in Simulation</Label>
              <RadioGroup
                row
                value={safeSimulation.food_support}
                onChange={(e) => {
                  const val = e.target.value === 'true';
                  setSimulation((curr: any) => ({ ...curr, food_support: val }));
                  setFoodOptions(val);
                }}
              >
                <FormControlLabel value={true} control={<Radio color="primary" />} label="Yes" />
                <FormControlLabel value={false} control={<Radio color="primary" />} label="No" />
              </RadioGroup>
            </FeatureToggle>

            {showFoodOptions && (
              <FeatureToggle className="sub-feature">
                <Label>How long food is effective</Label>
                <TextField
                  type="number"
                  variant="outlined"
                  style={{ width: '180px' }}
                  value={safeSimulation.food_interval}
                  onChange={(e) => setSimulation((curr: any) => ({ ...curr, food_interval: e.target.value === "" ? 0 : Number(e.target.value) }))}
                  InputProps={{ endAdornment: <InputAdornment position="end">{isPlural(safeSimulation.food_interval, timeText)}</InputAdornment> }}
                />
              </FeatureToggle>
            )}

            <FeatureToggle>
              <Label>Location Access</Label>
              <RadioGroup
                row
                value={safeSimulation.use_location}
                onChange={(e) => setSimulation((curr: any) => ({ ...curr, use_location: e.target.value === 'true' }))}
              >
                <FormControlLabel value={true} control={<Radio color="primary" />} label="Yes" />
                <FormControlLabel value={false} control={<Radio color="primary" />} label="No" />
              </RadioGroup>
            </FeatureToggle>

            <FeatureToggle>
              <Label>Beacon Access</Label>
              <RadioGroup
                row
                value={safeSimulation.use_beacons}
                onChange={(e) => setSimulation((curr: any) => ({ ...curr, use_beacons: e.target.value === 'true' }))}
              >
                <FormControlLabel value={true} control={<Radio color="primary" />} label="Yes" />
                <FormControlLabel value={false} control={<Radio color="primary" />} label="No" />
              </RadioGroup>
            </FeatureToggle>

            <FeatureToggle>
              <Label>Bluetooth Escape Limit</Label>
              <TextField
                type="number"
                variant="outlined"
                style={{ width: '180px' }}
                value={safeSimulation.escape_limit}
                onChange={(e) => setSimulation((curr: any) => ({ ...curr, escape_limit: e.target.value === "" ? 0 : Number(e.target.value) }))}
                InputProps={{ endAdornment: <InputAdornment position="end">seconds</InputAdornment> }}
              />
              <HelpTextInline>Time allowed to re-enable Bluetooth before being kicked from simulation</HelpTextInline>
            </FeatureToggle>

            <FeatureToggle>
              <Label>Debug features are enabled</Label>
              <RadioGroup
                row
                value={safeSimulation.debug_support}
                onChange={(e) => setSimulation((curr: any) => ({ ...curr, debug_support: e.target.value === 'true' }))}
              >
                <FormControlLabel value={true} control={<Radio color="primary" />} label="Yes" />
                <FormControlLabel value={false} control={<Radio color="primary" />} label="No" />
              </RadioGroup>
            </FeatureToggle>
          </GridContainer>
        </FormCard>
      </SectionWrapper>

      {/* Gamification & Scoring Section */}
      <SectionWrapper>
        <SecondaryHeader>Gamification & Scoring</SecondaryHeader>
        <FormCard>
          <Flex gap="24px">
            <FieldGroup style={{ flex: 1 }}>
              <Label>Minimum initial score *</Label>
              <TextField
                fullWidth
                type="number"
                variant="outlined"
                value={safeSimulation.init_score_min}
                onChange={(e) => setSimulation((curr: any) => ({ ...curr, init_score_min: e.target.value === "" ? 0 : Number(e.target.value) }))}
              />
            </FieldGroup>
            <FieldGroup style={{ flex: 1 }}>
              <Label>Maximum initial score *</Label>
              <TextField
                fullWidth
                type="number"
                variant="outlined"
                value={safeSimulation.init_score_max}
                onChange={(e) => setSimulation((curr: any) => ({ ...curr, init_score_max: e.target.value === "" ? 0 : Number(e.target.value) }))}
              />
            </FieldGroup>
          </Flex>
          <FieldGroup>
            <Label>Points to receive for each correct answer *</Label>
            <TextField
              fullWidth
              type="number"
              variant="outlined"
              value={safeSimulation.points_answer}
              onChange={(e) => setSimulation((curr: any) => ({ ...curr, points_answer: e.target.value === "" ? 0 : Number(e.target.value) }))}
            />
          </FieldGroup>

          {safeSimulation.rewards && (
            <div style={{ marginTop: '24px', padding: '24px', background: '#f0fff4', borderRadius: '12px', border: '1px solid #c6f6d5' }}>
              <Label style={{ color: '#276749', marginBottom: '16px' }}>Protection Rewards</Label>
              <Flex gap="24px" style={{ marginBottom: '16px' }}>
                <FieldGroup style={{ flex: 1 }}>
                  <Label>How long a PPE protects for *</Label>
                  <TextField
                    fullWidth
                    type="number"
                    variant="outlined"
                    value={safeSimulation.rewards.ppe_period}
                    onChange={(e) => {
                      const val = e.target.value === "" ? 0 : Number(e.target.value);
                      setSimulation((curr: any) => ({ ...curr, rewards: { ...curr.rewards, ppe_period: val } }));
                    }}
                    InputProps={{ endAdornment: <InputAdornment position="end">{isPlural(safeSimulation.rewards.ppe_period, timeText)}</InputAdornment> }}
                  />
                </FieldGroup>
                <FieldGroup style={{ flex: 1 }}>
                  <Label>How long a mask protects for *</Label>
                  <TextField
                    fullWidth
                    type="number"
                    variant="outlined"
                    value={safeSimulation.rewards.mask_period}
                    onChange={(e) => {
                      const val = e.target.value === "" ? 0 : Number(e.target.value);
                      setSimulation((curr: any) => ({ ...curr, rewards: { ...curr.rewards, mask_period: val } }));
                    }}
                    InputProps={{ endAdornment: <InputAdornment position="end">{isPlural(safeSimulation.rewards.mask_period, timeText)}</InputAdornment> }}
                  />
                </FieldGroup>
              </Flex>
              <Flex gap="20px">
                <FieldGroup style={{ flex: 1 }}>
                  <Label>Minimum # of quiz questions received to gain protection *</Label>
                  <TextField
                    fullWidth
                    type="number"
                    variant="outlined"
                    value={safeSimulation.rewards.min_questions}
                    onChange={(e) => {
                      const val = e.target.value === "" ? 0 : Number(e.target.value);
                      setSimulation((curr: any) => ({ ...curr, rewards: { ...curr.rewards, min_questions: val } }));
                    }}
                  />
                </FieldGroup>
                <FieldGroup style={{ flex: 1 }}>
                  <Label>Minimum quiz score to receive a mask *</Label>
                  <TextField
                    fullWidth
                    type="number"
                    variant="outlined"
                    value={safeSimulation.rewards.min_score_mask}
                    onChange={(e) => {
                      const val = e.target.value === "" ? 0 : Number(e.target.value);
                      setSimulation((curr: any) => ({ ...curr, rewards: { ...curr.rewards, min_score_mask: val } }));
                    }}
                  />
                </FieldGroup>
                <FieldGroup style={{ flex: 1 }}>
                  <Label>Minimum quiz score to receive a PPE *</Label>
                  <TextField
                    fullWidth
                    type="number"
                    variant="outlined"
                    value={safeSimulation.rewards.min_score_ppe}
                    onChange={(e) => {
                      const val = e.target.value === "" ? 0 : Number(e.target.value);
                      setSimulation((curr: any) => ({ ...curr, rewards: { ...curr.rewards, min_score_ppe: val } }));
                    }}
                  />
                </FieldGroup>
              </Flex>
            </div>
          )}
        </FormCard>
      </SectionWrapper>

      {/* Advanced Selection Sections */}
      <SectionWrapper>
        <SecondaryHeader>Advanced Management</SecondaryHeader>
        <FormCard>
          {/* Groups Section */}
          <FieldGroup style={{ marginBottom: '32px' }}>
            <Flex justify="space-between" alignItems="center" style={{ marginBottom: '16px' }}>
              <Label style={{ margin: 0 }}>Group Selection</Label>
              <RadioGroup
                row
                value={safeSimulation.group_support}
                onChange={(e) => {
                  const val = e.target.value === 'true';
                  setSimulation((curr: any) => ({ ...curr, group_support: val }));
                  setGroupOptions(val);
                }}
              >
                <FormControlLabel value={true} control={<Radio color="primary" />} label="Yes" />
                <FormControlLabel value={false} control={<Radio color="primary" />} label="No" />
              </RadioGroup>
            </Flex>

            {showGroupOptions && (
              <div style={{ padding: '24px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={safeSimulation.random_group}
                      onChange={(e) => setSimulation((curr: any) => ({ ...curr, random_group: e.target.checked }))}
                      color="primary"
                    />
                  }
                  label="Random Group"
                />
                <div style={{ marginTop: '24px' }}>
                  <Group
                    groups={safeSimulation.group_selection}
                    setValue={(val: any[]) => setSimulation((curr: any) => ({ ...curr, group_selection: val }))}
                  />
                </div>
              </div>
            )}
          </FieldGroup>

          {/* Interventions Section */}
          <FieldGroup style={{ marginBottom: '32px' }}>
            <Flex justify="space-between" alignItems="center" style={{ marginBottom: '16px' }}>
              <Label style={{ margin: 0 }}>Intervention Support</Label>
              <RadioGroup
                row
                value={safeSimulation.intervention_support}
                onChange={(e) => {
                  const val = e.target.value === 'true';
                  setSimulation((curr: any) => ({ ...curr, intervention_support: val }));
                  setInterventionOptions(val);
                }}
              >
                <FormControlLabel value={true} control={<Radio color="primary" />} label="Yes" />
                <FormControlLabel value={false} control={<Radio color="primary" />} label="No" />
              </RadioGroup>
            </Flex>

            {showInterventionOptions && (
              <div style={{ padding: '24px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                {interventionsLoaded ? (
                  <Interventions
                    title='Intervention list'
                    currentInterventions={safeSimulation.interventions}
                    allInterventions={interventions}
                    setValue={(val: any[]) => setSimulation((curr: any) => ({ ...curr, interventions: val }))}
                  />
                ) : (
                  <div style={{ color: '#a0aec0', fontStyle: 'italic' }}>No global interventions found. Create some in the Interventions tab first.</div>
                )}
              </div>
            )}
          </FieldGroup>

          {/* Shop Section */}
          <FieldGroup>
            <Flex justify="space-between" alignItems="center" style={{ marginBottom: '16px' }}>
              <Label style={{ margin: 0 }}>Shop Support</Label>
              <RadioGroup
                row
                value={safeSimulation.shop_support}
                onChange={(e) => {
                  const val = e.target.value === 'true';
                  setSimulation((curr: any) => ({ ...curr, shop_support: val }));
                  setShopOptions(val);
                }}
              >
                <FormControlLabel value={true} control={<Radio color="primary" />} label="Yes" />
                <FormControlLabel value={false} control={<Radio color="primary" />} label="No" />
              </RadioGroup>
            </Flex>

            {showShopOptions && (
              <div style={{ padding: '24px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <ShopItem
                  shopItems={safeSimulation.shop_items}
                  setValue={(val: any[]) => setSimulation((curr: any) => ({ ...curr, shop_items: val }))}
                />
              </div>
            )}
          </FieldGroup>
        </FormCard>
      </SectionWrapper>
    </div>
  );
};

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
`;

const FeatureToggle = styled.div`
  padding: 16px 20px;
  border: 1px solid #edf2f7;
  border-radius: 12px;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  justify-content: center;
  
  &:hover {
    border-color: #cbd5e0;
    background: #fbfbfb;
  }
  
  &.sub-feature {
    background: #f7fafc;
    border-style: dashed;
    grid-column: span 1;
  }

  .MuiRadio-root {
    padding: 4px;
  }
`;
